import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { LoanWrapper } from '@sorare/core/src/components/card/Loan';
import { StrokedCard } from '@sorare/core/src/components/card/StrokedCard';
import { UnsealableOverlay } from '@sorare/core/src/components/card/UnsealableOverlay';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import type { Color } from '@sorare/core/src/style/types';

import { BundledAuctionPreview } from '#src/components/auction/BundledAuctionPreview/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { StackedToken } from '#src/components/token/StackedToken/index.tsx';
import { useIsBoostAffordable } from '#src/hooks/useIsBoostAffordable.tsx';
import { useIsCardDroppable } from '#src/hooks/useIsCardDroppable.ts';
import type { MyCardsSelectionMode } from '#src/lib/my-cards.ts';

import type { TokenImg_anyCard } from './__generated__/index.graphql.ts';

const FlexCardWrapper = styled(ButtonBase)`
  width: 100%;
  position: relative;

  &.disabled {
    opacity: 0.5;
  }
`;

const colors: Record<string, { color: Color; glowColor: Color }> = {
  boost: {
    color: 'var(--c-rewards-blue)',
    glowColor: 'var(--c-rewards-blue)',
  },
  drop: {
    color: 'var(--c-rewards-orange)',
    glowColor: 'var(--c-rewards-orange)',
  },
  sealing: {
    color: 'var(--c-rewards-squad)',
    glowColor: 'var(--c-rewards-squad)',
  },
};

type Props = {
  card: TokenImg_anyCard;
  stackedTokensCount?: number;
  isBundledAuction?: boolean;
  isDesktopLayout: boolean;
  onToggleCard?: (
    slug: string,
    options?: { xpNeeded?: number; sealableFor?: number }
  ) => void;
  selected?: boolean;
  selectionMode?: MyCardsSelectionMode;
  totalXpNeeded?: number;
};

export const TokenImg = ({
  card,
  stackedTokensCount,
  isBundledAuction,
  isDesktopLayout,
  onToggleCard,
  selected,
  selectionMode,
  totalXpNeeded,
}: Props) => {
  const imageWidth = isDesktopLayout ? 320 : 160;

  const { isIosApp, canUseIosNativeCardPage } = useIsMobileApp();

  const isBoostAffordable = useIsBoostAffordable();
  const { isBoostable, xpNeeded, affordable, hasCooldown } = isBoostAffordable(
    card,
    totalXpNeeded
  );
  const isSealable = card.sealable;

  const isCardDroppable = useIsCardDroppable();
  const { isDroppable } = isCardDroppable(card);

  if (stackedTokensCount && stackedTokensCount > 1) {
    return (
      <StackedToken card={card} count={stackedTokensCount} width={imageWidth} />
    );
  }
  if (card.latestEnglishAuction && isBundledAuction) {
    return (
      <BundledAuctionPreview
        bundledAuction={card.latestEnglishAuction}
        displayOneCard={!isDesktopLayout}
      />
    );
  }

  const disabled =
    (selectionMode === 'boost' && !selected && !isBoostable) ||
    (selectionMode === 'drop' && !selected && !isDroppable) ||
    (selectionMode === 'sealing' && !selected && !isSealable);

  const showUnsealableOverlay =
    selectionMode === 'sealing' && !card.sealed && !card.sealable;
  return (
    <ConditionalWrapper
      Wrapper={UnsealableOverlay}
      wrap={Boolean(showUnsealableOverlay)}
      props={{ card }}
    >
      <FlexCardWrapper
        className={classNames({
          disabled,
        })}
        onClick={() => {
          const canClickBoost =
            selectionMode === 'boost' &&
            (affordable || selected) &&
            !hasCooldown;
          const canClickDrop =
            selectionMode === 'drop' && (selected || isDroppable);
          const canClickSeal = selectionMode === 'sealing' && !disabled;

          if (canClickBoost || canClickDrop || canClickSeal) {
            onToggleCard?.(card.slug, {
              xpNeeded,
              sealableFor: card.sealableFor || 0,
            });
          }
        }}
      >
        <StrokedCard
          active={!!selected}
          color={colors[selectionMode ?? 'boost'].color}
          strokeWidth={3}
          withGlow={{
            src: card.pictureUrl || '',
            color: colors[selectionMode ?? 'boost'].glowColor,
          }}
        >
          <LoanWrapper card={card}>
            <FlexCard
              card={card}
              width={imageWidth}
              withLink={
                !!(canUseIosNativeCardPage || !isIosApp) && !selectionMode
              }
            />
          </LoanWrapper>
        </StrokedCard>
      </FlexCardWrapper>
    </ConditionalWrapper>
  );
};

TokenImg.fragments = {
  anyCard: gql`
    fragment TokenImg_anyCard on AnyCardInterface {
      slug
      sealed
      sealable
      sealableFor
      latestEnglishAuction {
        id
        cards: anyCards {
          slug
        }
        ...BundledAuctionPreview_auction
      }
      ...FlexCard_anyCard
      ...StackedToken_anyCard
      ...useIsBoostAffordable_anyCard
      ...useIsCardDroppable_anyCard
      ...LoanWrapper_anyCard
      ...UnsealableOverlay_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${StackedToken.fragments.anyCard}
    ${BundledAuctionPreview.fragments.auction}
    ${useIsBoostAffordable.fragments.anyCard}
    ${useIsCardDroppable.fragments.anyCard}
    ${LoanWrapper.fragments.anyCard}
    ${UnsealableOverlay.fragments.anyCard}
  ` as TypedDocumentNode<TokenImg_anyCard>,
};
